import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { motion } from 'framer-motion';
import { Shield, Anchor, Flame, Award, Users, Globe, CheckCircle } from 'lucide-react';
import ScrollToTop from '../components/ScrollToTop';
import WhatsAppButton from '../components/WhatsAppButton';

const Home: React.FC = () => {
  const { t } = useLanguage();

  const stats = [
    { number: '15+', labelKey: 'experienceYears', icon: Award },
    { number: '500+', labelKey: 'completedProjects', icon: CheckCircle },
    { number: '500+', labelKey: 'happyCustomers', icon: Users },
    { number: '45+', labelKey: 'countries', icon: Globe }
  ];


  return (
    <div className="min-h-screen bg-slate-900 text-white">
      
      <section className="relative w-full h-screen overflow-hidden -mt-20 pt-20">
        <div className="absolute inset-0">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4, duration: 0.8 }}
            className="relative w-full h-full"
          >
            <video
              autoPlay
              loop
              muted
              playsInline
              className="w-full h-full object-cover opacity-50"
              poster="https://images.pexels.com/photos/1108101/pexels-photo-1108101.jpeg?auto=compress&cs=tinysrgb&w=1200"
            >
              <source src="/resimler/medyagemi1.mp4" type="video/mp4" />
              <source src="/resimler/ship.mp4" type="video/mp4" />
              Tarayıcınız video etiketini desteklemiyor.
            </video>
            
            <div className="absolute inset-0 bg-gradient-to-b from-slate-900/60 via-slate-900/40 to-slate-900/60"></div>
          </motion.div>
        </div>
        
       
        <div className="absolute inset-0 flex items-center justify-center z-10">
          <div className="text-center px-4">
            
            <motion.div
              initial={{ opacity: 0, scale: 0.8, rotate: -180 }}
              animate={{ opacity: 1, scale: 1, rotate: 0 }}
              transition={{ delay: 0.6, duration: 1.2, ease: "easeOut" }}
              whileHover={{ 
                rotate: [0, -2, 2, -2, 2, 0],
                transition: { 
                  duration: 0.5,
                  repeat: Infinity,
                  repeatType: "loop"
                }
              }}
              className="mb-12 flex justify-center"
            >
              <div className="relative cursor-pointer">
                <div className="w-20 h-20 bg-amber-400 bg-opacity-10 rounded-full flex items-center justify-center backdrop-blur-sm border border-amber-400 border-opacity-20">
                  <div className="w-12 h-12 relative">
                    {/* Compass  */}
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-10 h-10 relative">
                        
                       {/* North */}
                        <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 text-amber-400 text-sm font-bold opacity-70">N</div>
                        {/* South */}
                        <div className="absolute -bottom-4 left-1/2 transform -translate-x-1/2 text-amber-400 text-sm font-bold opacity-70">S</div>
                        {/* East */}
                        <div className="absolute top-1/2 -right-4 transform -translate-y-1/2 text-amber-400 text-sm font-bold opacity-70">E</div>
                        {/* West */}
                        <div className="absolute top-1/2 -left-4 transform -translate-y-1/2 text-amber-400 text-sm font-bold opacity-70">W</div>

                        
                        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-2 h-2 bg-amber-400 rounded-full opacity-80"></div>
                      
                        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                          <div className="w-0.5 h-3 bg-amber-400 transform -translate-y-1.5 opacity-80"></div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
                    <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8, duration: 0.8 }}
              className="text-4xl md:text-6xl font-bold text-white mb-6 relative"
            >
              {/* Sol yan yazı */}
              <span className="absolute -left-56 top-20 text-lg md:text-xl text-amber-400 font-medium transform -rotate-90 origin-bottom-left hidden lg:block">
                GÜVENLİK
              </span>
              
              {/* Ana başlık */}
              {t('heroTitle')}
              
              {/* Sağ yan yazı */}
              <span className="absolute -right-56 top-20 text-lg md:text-xl text-amber-400 font-medium transform rotate-90 origin-bottom-right hidden lg:block">
                KALİTE
              </span>
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.0, duration: 0.8 }}
              className="text-xl md:text-2xl text-slate-200 max-w-3xl mx-auto relative"
            >
              {/* Sol yan yazı */}
              <span className="absolute -left-48 top-16 transform -rotate-90 text-sm md:text-base text-slate-400 font-light hidden lg:block">
                ULUSLARARASI
              </span>
              
              {/* Alt başlık */}
              {t('heroSubtitle')}
              
              {/* Sağ yan yazı */}
              <span className="absolute -right-48 top-16 transform rotate-90 text-sm md:text-base text-slate-400 font-light hidden lg:block">
                UZMANLIK
              </span>
            </motion.p>
          </div>
        </div>
      </section>

      
      <section className="py-20 bg-slate-100">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
           
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-8">
                {t('whoAreWe')}
              </h2>
              <p className="text-lg text-slate-700 leading-relaxed mb-8">
                {t('aboutContent1')}
              </p>
              <Link to="/about">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-bold py-3 px-8 rounded-lg transition-colors duration-200"
                >
                  {t('moreInfo')}
                </motion.button>
              </Link>
            </motion.div>

         
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="relative"
            >
              <img
                src="/resimler/safety.jpg"
                alt="whoweare"
                className="w-full h-96 object-cover rounded-lg shadow-2xl"
              />
            </motion.div>
          </div>
        </div>
      </section>

      
      <section className="py-20 bg-slate-100">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
           
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="relative flex items-center"
            >
              <img
                src="/resimler/rakam.jpg"
                alt="no"
                className="w-full h-96 object-cover rounded-lg shadow-2xl"
              />
            </motion.div>

            
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="text-center"
            >
              <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-12">
                {t('numbersTitle')}
              </h2>
              <div className="grid grid-cols-2 gap-8">
                {stats.map((stat, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1, duration: 0.5 }}
                    viewport={{ once: true }}
                    className="text-center p-6 bg-white rounded-lg shadow-lg hover:shadow-xl transition-shadow duration-200"
                  >
                    <stat.icon className="h-12 w-12 text-amber-500 mx-auto mb-4" />
                    <div className="text-3xl font-bold text-slate-900 mb-2">{stat.number}</div>
                    <div className="text-slate-700 font-medium">{t(stat.labelKey)}</div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      
      <section className="py-16 bg-slate-900">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl font-bold text-center mb-12 text-amber-400">
              {t('servicesTitle')}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              <div className="bg-slate-800 p-6 rounded-lg hover:bg-slate-700 transition-colors duration-300 border border-slate-700">
                <Shield className="h-12 w-12 text-amber-400 mb-4" />
                <h3 className="text-xl font-bold mb-3 text-white">{t('fireDetectionService')}</h3>
                <p className="text-slate-300">{t('fireDetectionDesc')}</p>
              </div>
              <div className="bg-slate-800 p-6 rounded-lg hover:bg-slate-700 transition-colors duration-300 border border-slate-700">
                <Flame className="h-12 w-12 text-amber-400 mb-4" />
                <h3 className="text-xl font-bold mb-3 text-white">{t('fireExtinguishingService')}</h3>
                <p className="text-slate-300">{t('fireExtinguishingDesc')}</p>
              </div>
              <div className="bg-slate-800 p-6 rounded-lg hover:bg-slate-700 transition-colors duration-300 border border-slate-700">
                <Anchor className="h-12 w-12 text-amber-400 mb-4" />
                <h3 className="text-xl font-bold mb-3 text-white">{t('marineEquipmentService')}</h3>
                <p className="text-slate-300">{t('marineEquipmentDesc')}</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

       <section className="py-16 bg-slate-100">
        <div className="container mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl font-bold mb-6 text-slate-900">{t('ctaTitle')}</h2>
            <p className="text-xl mb-8 max-w-2xl mx-auto text-slate-700">
              {t('ctaDescription')}
            </p>
            <Link to="/contact">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-bold py-3 px-8 rounded-lg transition-colors duration-200"
              >
                {t('contactUs')}
              </motion.button>
            </Link>
          </motion.div>
        </div>
      </section>
      
      <ScrollToTop />
      <WhatsAppButton />
    </div>
  );
};

export default Home;